// ─── ControleDemandeMgd.jsx ──────────────────────────────────────────
// Confronter le plan enregistre a la demande d'analyses reellement envoyee.
//
// Le plan du SaaS est une prevision. La demande MGD est la commande. Sur le
// cycle de Clelia, les deux divergeaient de 108.60 CHF et personne ne l'a su
// avant la facture, six semaines plus tard, apres qu'Anissa avait deja
// facture sa cliente. Comme elle avance les frais, l'ecart sortait de sa
// poche.
//
// Le geste est celui qu'elle connait deja : elle depose un PDF. Celui-ci ne
// porte aucun resultat, aucune valeur biologique, seulement des codes. Il
// est lu dans le navigateur et jete : rien n'est stocke, rien ne part au
// serveur.

import { useCallback, useRef, useState } from 'react';
import { formulerEcart, lireDemandeMgd, reconcilierDemande } from '../services/demandeMgd';
import { LecturePdfError, lireTextePdf, messageLecture } from '../services/lireTextePdf';

const AMBRE = '#8a5a12';
const SAGE = '#2d5a3d';
const ROUGE = '#a33b2c';

const ligneStyle = {
  display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
  gap: 12, padding: '5px 0', fontSize: '.82rem',
  borderTop: '1px solid rgba(0,0,0,.07)',
};

const titreBlocStyle = {
  fontSize: '.72rem', color: '#888', margin: '10px 0 2px',
};

export default function ControleDemandeMgd({ planTests = [], catalogue = [], onAligner, onControle }) {
  const [etat, setEtat] = useState('repos'); // repos | lecture | resultat | erreur
  const [erreur, setErreur] = useState(null);
  const [rapport, setRapport] = useState(null);
  const inputRef = useRef(null);

  const traiter = useCallback(async (fichier) => {
    setEtat('lecture');
    setErreur(null);
    setRapport(null);
    try {
      const texte = await lireTextePdf(fichier);
      const demande = lireDemandeMgd(texte);
      if (!demande.lisible) {
        // Un PDF lisible mais qui n'est pas une demande d'analyses. Le dire
        // franchement vaut mieux qu'une reconciliation vide qui passerait
        // pour « tout concorde ».
        setErreur("Ce PDF ne ressemble pas à une demande d’analyses MGD : je n’y trouve pas la liste des analyses prescrites.");
        setEtat('erreur');
        return;
      }
      const reconciliation = reconcilierDemande(demande, planTests, catalogue);
      setRapport({ demande, reconciliation });
      setEtat('resultat');
      // Le controle a eu lieu, et c'est vrai meme quand tout concorde.
      // C'est justement ce cas-la qui ne laisse aucune trace ailleurs : il
      // ne change aucun montant, et sans cet appel le rappel resterait
      // affiche pour toujours sur un plan pourtant verifie.
      onControle?.(demande, reconciliation);
    } catch (e) {
      // Le detail technique voyage avec la raison : sur un echec qu'on ne
      // sait pas expliquer, une ligne obscure qu'Anissa nous recopie vaut
      // mieux qu'une hypothese qui l'envoie chercher au mauvais endroit.
      setErreur(e instanceof LecturePdfError
        ? messageLecture(e.raison, e.detail)
        : 'La lecture a échoué.');
      setEtat('erreur');
    }
  }, [planTests, catalogue, onControle]);

  const surFichier = (e) => {
    const f = e.target.files?.[0];
    // On vide l'input : sans ca, redeposer le meme fichier ne declenche rien.
    e.target.value = '';
    if (f) traiter(f);
  };

  const reconciliation = rapport?.reconciliation;
  const peutAligner = Boolean(
    onAligner && reconciliation
    && (reconciliation.ajoutees.length > 0 || reconciliation.manquantes.length > 0)
  );

  const aligner = () => {
    if (!peutAligner) return;
    const retires = new Set(reconciliation.manquantes.map(t => t.code));
    const codes = [
      ...planTests.map(t => t?.code).filter(c => c && !retires.has(c)),
      ...reconciliation.ajoutees.map(t => t.code),
    ];
    onAligner(codes, reconciliation);
  };

  return (
    <div style={{
      marginTop: 14,
      background: 'rgba(0,0,0,.02)',
      border: '1px solid rgba(0,0,0,.1)',
      borderRadius: 10,
      padding: '12px 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: '.85rem', color: '#333' }}>
          Contrôler avec la demande MGD
        </span>
        <span style={{ fontSize: '.72rem', color: '#999' }}>
          le PDF reste sur ton ordinateur
        </span>
      </div>

      <p style={{ fontSize: '.75rem', color: '#666', margin: '4px 0 10px', lineHeight: 1.5 }}>
        Dépose la demande d’analyses téléchargée depuis le portail. Je compare
        avec ce plan et je te dis ce qui a bougé.
      </p>

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={surFichier}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={etat === 'lecture'}
        style={{
          fontSize: '.8rem', padding: '6px 14px', borderRadius: 8,
          border: '1px solid rgba(0,0,0,.2)', background: '#fff',
          color: '#333', cursor: etat === 'lecture' ? 'wait' : 'pointer',
        }}
      >
        {etat === 'lecture' ? 'Lecture…' : 'Choisir le PDF'}
      </button>

      {etat === 'erreur' && (
        <p role="alert" style={{ fontSize: '.78rem', color: ROUGE, margin: '10px 0 0', lineHeight: 1.5 }}>
          {erreur}
        </p>
      )}

      {reconciliation && reconciliation.concordant && (
        <p style={{ fontSize: '.8rem', color: SAGE, margin: '10px 0 0' }}>
          La demande {rapport.demande.numeroDemande ? `n° ${rapport.demande.numeroDemande} ` : ''}
          correspond au plan. Rien à corriger.
        </p>
      )}

      {reconciliation && !reconciliation.concordant && (
        <div style={{ marginTop: 10 }}>
          <p style={{ fontSize: '.8rem', color: AMBRE, margin: '0 0 4px', fontWeight: 500 }}>
            {formulerEcart(reconciliation)}
          </p>

          {reconciliation.ajoutees.length > 0 && (
            <>
              <p style={titreBlocStyle}>Commandées, absentes du plan</p>
              {reconciliation.ajoutees.map(t => (
                <div key={t.code} style={ligneStyle}>
                  <span style={{ color: '#333' }}>{t.display_name || t.code}</span>
                  <span style={{ color: AMBRE, fontVariantNumeric: 'tabular-nums' }}>
                    +{t.cost_anissa_chf} CHF
                  </span>
                </div>
              ))}
            </>
          )}

          {reconciliation.manquantes.length > 0 && (
            <>
              <p style={titreBlocStyle}>Prévues, pas sur la demande</p>
              {reconciliation.manquantes.map(t => (
                <div key={t.code} style={ligneStyle}>
                  <span style={{ color: '#777' }}>{t.display_name || t.code}</span>
                  <span style={{ color: '#999', fontVariantNumeric: 'tabular-nums' }}>
                    {t.cost_anissa_chf != null ? `−${t.cost_anissa_chf} CHF` : ''}
                  </span>
                </div>
              ))}
            </>
          )}

          {reconciliation.inconnues.length > 0 && (
            <p role="alert" style={{
              fontSize: '.76rem', color: ROUGE, margin: '10px 0 0', lineHeight: 1.5,
            }}>
              Je ne reconnais pas {reconciliation.inconnues.length > 1 ? 'ces codes' : 'ce code'} :{' '}
              <strong>{reconciliation.inconnues.join(', ')}</strong>. Ils sont sur la demande mais
              pas dans le catalogue, donc leur coût n’est compté nulle part. À ajouter au catalogue
              avant de facturer.
            </p>
          )}

          {peutAligner && (
            <button
              type="button"
              onClick={aligner}
              style={{
                marginTop: 12, fontSize: '.8rem', padding: '6px 14px', borderRadius: 8,
                border: `1px solid ${AMBRE}`, background: 'rgba(138,90,18,.07)',
                color: AMBRE, cursor: 'pointer',
              }}
            >
              Aligner le plan sur la demande
            </button>
          )}
        </div>
      )}
    </div>
  );
}
